import { format, parseISO } from 'date-fns';
import { addDays } from '../../utils/timeUtils';
import { OUTREACH_SNOOZE_PRESET_DAYS } from '../../constants/outreachConstants';

/**
 * GP-1365 — the date rules behind "Log Outreach Attempt", kept as pure functions
 * on plain `YYYY-MM-DD` strings so they can be unit-tested and so the client and
 * the server can be checked against each other.
 *
 * Every comparison here is a STRING comparison (YYYY-MM-DD sorts correctly) — no
 * Date object is ever built from a date-only value, per CLAUDE.md's date rules.
 * "Today" must be the PHARMACY's today (see getPharmacyTodayString), not the
 * browser's, because the server validates against the pharmacy calendar day.
 */

/** Display form used in the dialog's messages, e.g. `Aug 14, 2026`. */
export const formatSnoozeDate = (dateStr: string): string =>
  format(parseISO(`${dateStr}T00:00:00`), 'MMM dd, yyyy');

/**
 * The EARLIEST due date a deferral may use: tomorrow.
 *
 * Not today. A task due today is still Overdue on both sides — the stat tile
 * (`ManualTasksService`: `dueDate <= todayStr`) and the row badge
 * (`isPast(startOfDay(dueDate))`). Allowing today would let the counselor save,
 * see "Outreach logged", and find the task exactly where it was: the dead end
 * this feature exists to remove, with a success message on top.
 */
export const snoozeFloor = (today: string): string => addDays(today, 1);

/**
 * Whether ANY date satisfies both rules (on/after tomorrow, on/before the
 * delivery). False only when the delivery is today: the ceiling and the floor
 * cross. The dialog disables the action and says so rather than offering a save
 * that cannot work.
 */
export const hasLegalSnoozeDate = (today: string, ceiling?: string): boolean =>
  !ceiling || ceiling >= snoozeFloor(today);

/** A quick-defer preset resolved against the pharmacy's today. */
export const snoozePresetDate = (today: string, days: number): string =>
  addDays(today, days);

/**
 * The latest date a follow-up may be scheduled for, or `undefined` when there is
 * none.
 *
 * The ceiling exists so a follow-up is not booked for after the medication
 * arrives. A delivery date that has ALREADY passed cannot do that job, and
 * enforcing it then would leave no legal date at all — which would put the
 * counselor back in the "complete it or decline it" dead end this feature
 * removes. Mirrors the server rule in ManualTasksService.logOutreachAttempt.
 */
export const snoozeCeiling = (
  deliveryDate: string | undefined,
  today: string,
): string | undefined => {
  const delivery = deliveryDate ? deliveryDate.slice(0, 10) : undefined;
  return delivery && delivery >= today ? delivery : undefined;
};

/**
 * Seed value for the date picker: the first preset that still lands on or before
 * the ceiling. When delivery is sooner than every preset, fall back to the
 * ceiling itself — never past it — and let the counselor see and change it.
 *
 * Returns '' when no legal date exists (delivery today). Deliberately NOT a
 * fallback to today: seeding a value the validator and the server both reject
 * would be a silent substitution of a known-bad date.
 */
export const defaultSnoozeDueDate = (today: string, ceiling?: string): string => {
  if (!hasLegalSnoozeDate(today, ceiling)) return '';
  const floor = snoozeFloor(today);
  const fits = OUTREACH_SNOOZE_PRESET_DAYS
    .map((days) => snoozePresetDate(today, days))
    .filter((d) => d >= floor && (!ceiling || d <= ceiling));
  if (fits.length) return fits[0];
  // No preset fits, but the ceiling itself is legal (delivery between tomorrow
  // and the first preset).
  return ceiling as string;
};

/**
 * Client-side mirror of the server's rules, so the counselor sees the reason
 * before the round trip. Returns '' when the date is acceptable. The server
 * enforces the same rules regardless of what the client allows.
 */
export const validateSnoozeDueDate = (
  dueDate: string,
  today: string,
  ceiling?: string,
): string => {
  if (!dueDate) return 'Pick a new due date';
  if (!hasLegalSnoozeDate(today, ceiling)) {
    return `The delivery is today (${formatSnoozeDate(today)}) — there is no later date this follow-up could use`;
  }
  if (dueDate < today) {
    return `Due date cannot be in the past (today is ${formatSnoozeDate(today)})`;
  }
  if (dueDate < snoozeFloor(today)) {
    // i.e. exactly today. Saving this would report success and still be overdue.
    return `A follow-up due today would still be overdue — pick ${formatSnoozeDate(snoozeFloor(today))} or later`;
  }
  if (ceiling && dueDate > ceiling) {
    return `Follow-up must be on or before the delivery date (${formatSnoozeDate(ceiling)})`;
  }
  return '';
};
